import { config } from "@/config";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";

// Uploads a file (e.g. an Action screenshot) and returns the stored file's details.
async function uploadFileWithOptions({
  file,
  headers,
}: {
  file: File;
  headers: Record<string, string>;
}): Promise<any> {
  const formData = new FormData();
  if (file) formData.append("file", file);
  const response = await apiFetch(`${config.isometricApiUrl}/upload`, {
    method: "POST",
    body: formData,
    headers,
  });

  const res = await response.json();
  if (!response.ok) {
    throw new Error("Failed to send the message.");
  }
  return res;
}

export function useUploadFileWithOptions(
  onSettled?: (res: any | undefined, error: Error | null) => void
) {
  const { getAuthHeaders } = useAuthHeaders();

  return useMutation({
    mutationFn: (params: { file: File }) => {
      const headers = getAuthHeaders(false);
      return uploadFileWithOptions({ ...params, headers });
    },
    onSettled,
  });
}

export async function getSignedUrl(
  path: string,
  headers: Record<string, string>
) {
  const url = `${
    config.isometricApiUrl
  }/documents/get-signed-url/${encodeURIComponent(`${path}`)}`;

  const response = await apiFetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
  });

  // Check if the response is ok (status code in the range 200-299)
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const result = await response.text();
  return result;
}

export function useSignedUrl(awsUrl: string, enabled?: boolean) {
  const { getAuthHeaders } = useAuthHeaders();
  const rawPath = awsUrl.split("amazonaws.com/")[1];
  let path = rawPath;
  try {
    if (rawPath) path = decodeURIComponent(rawPath);
  } catch {
    /* keep raw if not valid %-encoding */
  }

  return useQuery({
    queryKey: ["signedUrl", awsUrl],
    queryFn: () => {
      const headers = getAuthHeaders();
      return getSignedUrl(path, headers);
    },
    enabled: !!enabled,
  });
}
